export const ACCENT = "#14B8A6";

export const fmtHM = (m) => (m == null ? "—" : `${Math.floor(Math.abs(m) / 60)}h ${String(Math.round(Math.abs(m) % 60)).padStart(2, "0")}m`);
export const fmtSignedHM = (m) => (m == null ? "—" : `${m < 0 ? "−" : "+"}${fmtHM(m)}`);
export const fmtClock = (m) => (m == null ? "—" : `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m % 60)).padStart(2, "0")}`);
export const fmtPct = (v) => (v == null ? "—" : `${Math.round(v)}%`);
export const median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
export const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const dowOf = (d) => new Date(`${d}T00:00:00`).getDay();
export const dowName = (d) => DOW[dowOf(d)];
export const isWeekend = (d) => [0, 6].includes(dowOf(d));

export const covLabel = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "no data");

export function presetsFor(data) {
  const latest = [data.coverage.qatar, data.coverage.india].filter(Boolean).sort().pop() || new Date().toISOString().slice(0, 10);
  const month = latest.slice(0, 7);
  const earliest = data.days.length ? data.days.reduce((a, r) => (r.d < a ? r.d : a), latest) : `${month}-01`;
  return {
    month, latest,
    list: [
      { id: "mtd", label: "MTD", from: `${month}-01`, to: latest },
      { id: "all", label: "All", from: earliest, to: latest },
    ],
  };
}

export const filterDays = (data, { from, to, site }) =>
  data.days.filter((r) => r.d >= from && r.d <= to && (site === "All" || r.s === site));

/* Per-day derivations against configurable targets */
export function deriveDay(r, t) {
  const req = t.hours * 60;
  const valid = r.a === "P" && r.wm != null && r.f !== "rev";
  const late = r.ti != null && r.ss != null ? Math.max(0, r.ti - r.ss) : null;
  const isLate = late != null && late > t.grace;
  const end = r.ss != null ? r.ss + req : null;
  const early = r.to != null && end != null ? Math.max(0, end - r.to) : null;
  const isEarly = early != null && early > t.grace;
  return { valid, late, isLate, early, isEarly, met: valid && r.wm >= req, deficit: valid ? r.wm - req : 0 };
}

export function kpisOf(days, t) {
  const present = days.filter((r) => r.a === "P");
  const absent = days.filter((r) => r.a === "A");
  const der = days.map((r) => ({ r, k: deriveDay(r, t) }));
  const valid = der.filter((x) => x.k.valid);
  const mins = valid.map((x) => x.r.wm);
  const met = valid.filter((x) => x.k.met).length;
  const withSs = der.filter((x) => x.k.late != null && x.r.a === "P");
  const lateN = withSs.filter((x) => x.k.isLate).length;
  const earlyChk = der.filter((x) => x.k.early != null && x.r.a === "P");
  const earlyN = earlyChk.filter((x) => x.k.isEarly).length;
  const corrupt = days.filter((r) => r.f).length;
  const wkAbs = absent.filter((r) => !isWeekend(r.d)).length;
  const wkPres = present.filter((r) => !isWeekend(r.d)).length;
  return {
    present: present.length, absent: absent.length, validDays: valid.length,
    pctMet: valid.length ? (100 * met) / valid.length : null,
    avgMin: mean(mins), medMin: median(mins),
    deficit: valid.reduce((a, x) => a + x.k.deficit, 0),
    latePct: withSs.length ? (100 * lateN) / withSs.length : null, lateN,
    earlyPct: earlyChk.length ? (100 * earlyN) / earlyChk.length : null, earlyN,
    corrupt, corruptPct: present.length ? (100 * corrupt) / present.length : null,
    wkAbs, monFriPct: wkPres + wkAbs ? (100 * wkPres) / (wkPres + wkAbs) : null,
    medIn: median(present.map((r) => r.ti).filter((v) => v != null)),
    medOut: median(present.map((r) => r.to).filter((v) => v != null)),
  };
}

export const BUCKETS = [
  { label: "10h 00m and above", min: 600 },
  { label: "9h 00m – 9h 59m", min: 540 },
  { label: "8h 30m – 8h 59m", min: 510 },
  { label: "8h 00m – 8h 29m", min: 480 },
  { label: "7h 00m – 7h 59m", min: 420 },
  { label: "6h 00m – 6h 59m", min: 360 },
  { label: "Under 6h 00m", min: 0 },
];

export function distribution(days, t) {
  const valid = days.filter((r) => deriveDay(r, t).valid);
  const counts = BUCKETS.map(() => 0);
  valid.forEach((r) => {
    const i = BUCKETS.findIndex((b) => r.wm >= b.min);
    counts[i === -1 ? BUCKETS.length - 1 : i] += 1;
  });
  return BUCKETS.map((b, i) => ({ ...b, n: counts[i], pct: valid.length ? (100 * counts[i]) / valid.length : 0 }));
}

export function monthlyRollup(days, t) {
  const by = {};
  days.forEach((r) => (by[r.d.slice(0, 7)] = by[r.d.slice(0, 7)] || []).push(r));
  return Object.entries(by).sort(([a], [b]) => (a < b ? -1 : 1)).map(([m, rows]) => {
    const k = kpisOf(rows, t);
    return { m, ...k };
  });
}

export function absenceBlocks(days) {
  const abs = [...new Set(days.filter((r) => r.a === "A").map((r) => r.d))].sort();
  const blocks = [];
  let cur = null;
  abs.forEach((d) => {
    if (cur && (new Date(`${d}T00:00:00`) - new Date(`${cur.to}T00:00:00`)) === 86400000) {
      cur.to = d; cur.len += 1; cur.weekdays += isWeekend(d) ? 0 : 1;
    } else {
      cur = { from: d, to: d, len: 1, weekdays: isWeekend(d) ? 0 : 1 };
      blocks.push(cur);
    }
  });
  return blocks.filter((b) => b.len >= 3).sort((a, b) => b.len - a.len);
}

export function absDowClustering(days) {
  const counts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0 };
  days.filter((r) => r.a === "A" && !isWeekend(r.d)).forEach((r) => { counts[dowName(r.d)] += 1; });
  return counts;
}

export function agentRows(days, agents, t) {
  const by = {};
  days.forEach((r) => (by[r.c] = by[r.c] || []).push(r));
  return Object.entries(by).map(([c, rows]) => {
    const a = agents[c] || {};
    const k = kpisOf(rows, t);
    return { c, name: a.name || c, tl: a.tl || "—", site: a.site || rows[0].s, sub: a.sub || "", ...k };
  });
}

export function tlRows(rows) {
  const by = {};
  rows.forEach((r) => {
    const g = (by[r.tl] = by[r.tl] || { tl: r.tl, hc: 0, monFri: [], pctMet: [], latePct: [], medMin: [], corrupt: 0 });
    g.hc += 1; g.corrupt += r.corrupt;
    if (r.monFriPct != null) g.monFri.push(r.monFriPct);
    if (r.pctMet != null) g.pctMet.push(r.pctMet);
    if (r.latePct != null) g.latePct.push(r.latePct);
    if (r.medMin != null) g.medMin.push(r.medMin);
  });
  return Object.values(by).map((g) => ({
    tl: g.tl, hc: g.hc, corrupt: g.corrupt,
    monFriPct: mean(g.monFri), pctMet: mean(g.pctMet), latePct: mean(g.latePct), medMin: median(g.medMin),
  }));
}

export const RAG = { green: "#10B981", amber: "#F59E0B", red: "#F43F5E" };

export function ragOf(k) {
  const grade = (v, g, a, invert = false) => {
    if (v == null) return "amber";
    const x = invert ? -v : v;
    return x >= (invert ? -g : g) ? "green" : x >= (invert ? -a : a) ? "amber" : "red";
  };
  return [
    { dim: "Duration discipline", rag: grade(k.pctMet, 60, 40), evidence: `${fmtPct(k.pctMet)} of valid days met the bar · median ${fmtHM(k.medMin)}` },
    { dim: "Punctuality", rag: grade(k.latePct, 20, 40, true), evidence: `${fmtPct(k.latePct)} late beyond grace · median in-punch ${fmtClock(k.medIn)}` },
    { dim: "Attendance / availability", rag: grade(k.monFriPct, 90, 75), evidence: `${fmtPct(k.monFriPct)} Mon–Fri attendance · ${k.wkAbs} weekday absences` },
    { dim: "Shift-end discipline", rag: grade(k.earlyPct, 15, 30, true), evidence: `${fmtPct(k.earlyPct)} early departures · median out-punch ${fmtClock(k.medOut)}` },
    { dim: "Data hygiene", rag: grade(k.corruptPct, 2, 5, true), evidence: `${k.corrupt} corrupt punch records (${fmtPct(k.corruptPct)} of present days)` },
  ];
}
